import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { getUser } from '../../../auth.js';
import { getNotifications } from '../../../api/customerDashboardApi.js';
import { logout } from '../../../api/authApi.js';
import { connectAndGetSocket } from '../../../socket.js';
import ConfirmModal from '../../../components/ConfirmModal.js';
import NotificationModal from './NotificationModal.jsx';
import BubbleLoader from './BubbleLoader.jsx';
import { classifyPortalError } from '../utils/apiError.js';
import { showErrorToast } from '../utils/toast.js';
import vocovoLogoWhite from '../../../assets/VocovoLogoNavbarWhite.png';
import './CustomerTopNav.css';

const NAV_ITEMS = [
  { to: '/customer/dashboard', label: 'Dashboard', end: true },
  { to: '/customer/orders', label: 'My Orders' },
  { to: '/customer/price-listing', label: 'Price List' },
];

export default function CustomerTopNav() {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [query, setQuery] = useState('');
  const [notifItems, setNotifItems] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifModalOpen, setNotifModalOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const notifRef = useRef(null);
  const userMenuRef = useRef(null);

  useEffect(() => {
    setCustomer(getUser());
  }, []);

  useEffect(() => {
    const socket = connectAndGetSocket();
    if (!socket) return;
    
    const handler = () => setHasUnread(true);
    socket.on("notification", handler);
    socket.on("new_unread_notification", handler);

    const requestUnread = () => {
      console.log("Emitting get_unread_status");
      socket.emit("get_unread_status");
    };
    if (socket.connected) {
      requestUnread();
    } else {
      socket.once("connect", requestUnread);
    }

    return () => {
      socket.off("notification", handler);
      socket.off("new_unread_notification", handler);
    };
  }, []);

  const handleClickOutside = useCallback((e) => {
    if (notifRef.current && !notifRef.current.contains(e.target)) {
      setNotifOpen(false);
    }
    if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
      setUserMenuOpen(false);
    }
  }, []);

  useEffect(() => {
    if (notifOpen || userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notifOpen, userMenuOpen, handleClickOutside]);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const LockIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );

  const LogoutIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );

  return (
    <header className="customer-portal-topnav">
      <img
        src={vocovoLogoWhite}
        alt="VoCoVo"
        className="customer-portal-topnav__logo"
        onClick={() => navigate('/customer/dashboard')}
        style={{ cursor: 'pointer' }}
      />
      <div className="customer-portal-topnav__divider" />

      <nav className="customer-portal-topnav__links">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              'customer-portal-topnav__link' + (isActive ? ' customer-portal-topnav__link--active' : '')
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="customer-portal-topnav__spacer" />

      <form className="customer-portal-topnav__search" role="search" onSubmit={(e) => e.preventDefault()}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="text"
          placeholder="Search orders, products…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </form>

      <div className="customer-portal-topnav__notif-wrapper" ref={notifRef}>
        <button
          className="customer-portal-topnav__icon-btn"
          type="button"
          aria-label="Notifications"
          onClick={async () => {
            const willOpen = !notifOpen;
            setNotifOpen(willOpen);
            setHasUnread(false);
            if (willOpen) {
              setNotifLoading(true);
              try {
                const res = await getNotifications(5);
                setNotifItems(res.data);
              } catch (err) {
                showErrorToast(classifyPortalError(err).message || "Couldn't load notifications. Please try again.");
              } finally {
                setNotifLoading(false);
              }
            }
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
          {hasUnread && <span className="customer-portal-topnav__badge" />}
        </button>

        {notifOpen && (
          <div className="customer-portal-topnav__notif-dropdown">
            <div className="customer-portal-topnav__notif-header">
              <div className="customer-portal-topnav__notif-title">Notifications</div>
              <button className="customer-portal-topnav__notif-link" type="button" onClick={() => { setNotifModalOpen(true); setNotifOpen(false); }}>
                View all
              </button>
            </div>
            <div className="customer-portal-topnav__notif-list">
              {notifLoading ? (
                <div className="customer-portal-topnav__notif-loader"><BubbleLoader /></div>
              ) : notifItems.length === 0 ? (
                <div className="customer-portal-topnav__notif-empty">No notifications</div>
              ) : (
                notifItems.map((n, i) => {
                  const isClickable = n.url?.startsWith('/customer');
                  const content = (
                    <>
                      <div className="customer-portal-topnav__notif-item-title">{n.title}</div>
                      <div className="customer-portal-topnav__notif-item-meta">{n.message}</div>
                    </>
                  );
                  return isClickable ? (
                    <button
                      key={n.cursor ?? i}
                      type="button"
                      className="customer-portal-topnav__notif-item customer-portal-topnav__notif-item--clickable"
                      onClick={() => { setNotifOpen(false); navigate(n.url); }}
                    >
                      {content}
                    </button>
                  ) : (
                    <div key={n.cursor ?? i} className="customer-portal-topnav__notif-item">
                      {content}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      <div className="customer-portal-topnav__user-wrapper" ref={userMenuRef}>
        <button className="customer-portal-topnav__user" type="button" onClick={() => setUserMenuOpen((v) => !v)}>
          <span className="customer-portal-topnav__avatar">{customer?.name?.charAt(0)?.toUpperCase() ?? '•'}</span>
          <span className="customer-portal-topnav__user-meta">
            <span className="customer-portal-topnav__user-name">{customer?.name ?? 'Loading…'}</span>
            <span className="customer-portal-topnav__user-role">{customer?.role ?? ''}</span>
          </span>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transform: userMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        {userMenuOpen && (
          <div className="customer-portal-topnav__user-dropdown">
            <div className="customer-portal-topnav__user-card">
              <span className="customer-portal-topnav__avatar customer-portal-topnav__avatar--lg">
                {customer?.name?.charAt(0)?.toUpperCase() ?? '•'}
              </span>
              <div className="customer-portal-topnav__user-card-meta">
                <div className="customer-portal-topnav__user-card-name">{customer?.name ?? ''}</div>
                <div className="customer-portal-topnav__user-card-email">{customer?.email ?? ''}</div>
              </div>
            </div>

            <div className="customer-portal-topnav__menu-divider" />

            <button className="customer-portal-topnav__menu-item" type="button" onClick={() => { setUserMenuOpen(false); navigate('/auth/update-password'); }}>
              <LockIcon />
              Update Password
            </button>

            <button className="customer-portal-topnav__menu-item customer-portal-topnav__menu-item--danger" type="button" onClick={() => setLogoutConfirmOpen(true)}>
              <LogoutIcon />
              Logout
            </button>
          </div>
        )}
      </div>

      <ConfirmModal
        open={logoutConfirmOpen}
        title="Logout"
        message="Are you sure you want to log out?"
        confirmText="Logout"
        cancelText="Cancel"
        confirmType="danger"
        onConfirm={handleLogout}
        onCancel={() => setLogoutConfirmOpen(false)}
      />
      <NotificationModal open={notifModalOpen} onClose={() => setNotifModalOpen(false)} />
    </header>
  );
}
